import React, { useMemo, useState } from 'react';
import {
  Alert,
  Badge,
  Button,
  Drawer,
  Dropdown,
  Form,
  Input,
  Modal,
  Popover,
  Select,
  Space,
  Table,
  Typography,
  message,
} from 'antd';
import { PlusOutlined, ReloadOutlined } from '@ant-design/icons';
import type { MpWarehouse, RemoteMpWarehouse } from '../../api/MpWarehouses';
import * as MpWarehousesApi from '../../api/MpWarehouses';
import type { SelfWarehouse } from '../../api/SelfWarehouses';
import { MetaGroupsDrawer } from './MetaGroupsDrawer';
const { Text, Title, Paragraph, Link: AntLink } = Typography;

type Props = {
  marketplace: string;
  rows: MpWarehouse[];
  remote: RemoteMpWarehouse[];
  selfWarehouses: SelfWarehouse[];
  updatedAt?: string | null;
  loading?: boolean;
  onChanged: (rows: MpWarehouse[]) => void;
  onRefreshStatuses: () => Promise<MpWarehouse[]>;
};

type DrawerMode = 'create' | 'edit';

function warehouseState(row: MpWarehouse): {
  status: 'success' | 'default' | 'warning' | 'error';
  text: string;
} {
  if (row.status === 'archived') {
    return { status: 'error', text: 'Архив на МП' };
  }
  if (!row.self_warehouse_ids?.length) {
    return { status: 'warning', text: 'Требуется настройка' };
  }
  if (!row.is_active) {
    return { status: 'default', text: 'Отключён' };
  }
  return { status: 'success', text: 'Активен' };
}

function formatWhen(iso?: string | null) {
  if (!iso) return null;
  try {
    return new Date(iso).toLocaleString('ru', {
      day: 'numeric',
      month: 'long',
      hour: '2-digit',
      minute: '2-digit',
    });
  } catch {
    return iso;
  }
}

export const WarehousesTab = ({
  marketplace,
  rows,
  remote,
  selfWarehouses,
  updatedAt,
  loading,
  onChanged,
  onRefreshStatuses,
}: Props) => {
  const [drawerOpen, setDrawerOpen] = useState(false);
  const [drawerMode, setDrawerMode] = useState<DrawerMode>('create');
  const [editing, setEditing] = useState<MpWarehouse | null>(null);
  const [externalId, setExternalId] = useState<string | null>(null);
  const [selfIds, setSelfIds] = useState<number[]>([]);
  const [supplyTokens, setSupplyTokens] = useState<Record<number, string>>({});
  const [saving, setSaving] = useState(false);
  const [refreshing, setRefreshing] = useState(false);
  const [helpOpen, setHelpOpen] = useState(false);
  const [metaMp, setMetaMp] = useState<MpWarehouse | null>(null);

  const usedExternalIds = useMemo(
    () => new Set(rows.map((r) => r.external_warehouse_id)),
    [rows],
  );

  const availableRemote = useMemo(
    () => remote.filter((r) => !usedExternalIds.has(r.external_id)),
    [remote, usedExternalIds],
  );

  const selfOptions = selfWarehouses.map((s) => ({
    value: s.id,
    label: `${s.name}${s.is_default ? ' · default' : ''}`,
  }));

  const openCreate = () => {
    setDrawerMode('create');
    setEditing(null);
    setExternalId(null);
    setSelfIds([]);
    setSupplyTokens({});
    setDrawerOpen(true);
  };

  const openEdit = (row: MpWarehouse) => {
    setDrawerMode('edit');
    setEditing(row);
    setExternalId(row.external_warehouse_id);
    setSelfIds([...row.self_warehouse_ids]);
    const tokens: Record<number, string> = {};
    for (const w of row.self_warehouses || []) {
      if (w.supply_token) tokens[w.id] = w.supply_token;
    }
    setSupplyTokens(tokens);
    setDrawerOpen(true);
  };

  const closeDrawer = () => {
    setDrawerOpen(false);
    setEditing(null);
  };

  const buildLinksPayload = () =>
    selfIds.map((id) => ({
      self_warehouse_id: id,
      supply_token: (supplyTokens[id] || '').trim() || null,
    }));

  const onSelfIdsChange = (next: number[]) => {
    setSelfIds(next);
    setSupplyTokens((prev) => {
      const kept: Record<number, string> = {};
      for (const id of next) {
        if (prev[id]) kept[id] = prev[id];
      }
      return kept;
    });
  };

  const saveDrawer = async () => {
    if (drawerMode === 'create') {
      if (!externalId) {
        message.warning('Выберите склад Ozon / МП');
        return;
      }
      if (!selfIds.length) {
        message.warning('Выберите хотя бы один физический склад');
        return;
      }
      setSaving(true);
      try {
        const created = await MpWarehousesApi.create({
          marketplace,
          external_warehouse_id: externalId,
          self_warehouse_links: buildLinksPayload(),
        });
        onChanged([...rows, created]);
        message.success(`Добавлен склад «${created.name}»`);
        closeDrawer();
      } catch (e: any) {
        message.error(e?.message || String(e));
      } finally {
        setSaving(false);
      }
      return;
    }

    if (!editing) return;
    setSaving(true);
    try {
      const updated = await MpWarehousesApi.update(editing.id, {
        self_warehouse_links: buildLinksPayload(),
      });
      onChanged(rows.map((r) => (r.id === updated.id ? updated : r)));
      message.success('Сопоставление сохранено');
      closeDrawer();
    } catch (e: any) {
      message.error(e?.message || String(e));
    } finally {
      setSaving(false);
    }
  };

  const confirmDisable = (row: MpWarehouse) => {
    Modal.confirm({
      title: 'Отключить склад?',
      content: (
        <div>
          <p>Целевой остаток будет установлен в 0.</p>
          <p>На маркетплейс будут отправлены нулевые остатки.</p>
        </div>
      ),
      okText: 'Отключить склад',
      okButtonProps: { danger: true },
      cancelText: 'Отмена',
      onOk: async () => {
        const updated = await MpWarehousesApi.update(row.id, {
          is_active: false,
        });
        onChanged(rows.map((r) => (r.id === updated.id ? updated : r)));
        message.success(`Склад «${updated.name}» отключён`);
      },
    });
  };

  const enableWarehouse = async (row: MpWarehouse) => {
    const updated = await MpWarehousesApi.update(row.id, { is_active: true });
    onChanged(rows.map((r) => (r.id === updated.id ? updated : r)));
    message.success(`Склад «${updated.name}» активен`);
  };

  const refresh = async () => {
    setRefreshing(true);
    try {
      const synced = await onRefreshStatuses();
      onChanged(synced);
      message.success('Состояния обновлены');
    } catch (e: any) {
      message.error(e?.message || String(e));
    } finally {
      setRefreshing(false);
    }
  };

  return (
    <div>
      <div
        style={{
          display: 'flex',
          justifyContent: 'space-between',
          gap: 12,
          flexWrap: 'wrap',
          marginBottom: 12,
          alignItems: 'flex-start',
        }}
      >
        <div>
          <Title level={5} style={{ margin: 0 }}>
            Склады {marketplace === 'ozon' ? 'Ozon' : 'МП'}
          </Title>
          <Paragraph type="secondary" style={{ margin: '4px 0 0' }}>
            Сопоставление складов маркетплейса с физическими складами.
          </Paragraph>
          <Text type="secondary" style={{ fontSize: 12 }}>
            {updatedAt
              ? `Последнее обновление: ${formatWhen(updatedAt)}`
              : 'Статусы ещё не обновлялись в этой сессии'}
          </Text>
        </div>
        <Space wrap>
          <Button
            icon={<ReloadOutlined />}
            loading={refreshing || loading}
            onClick={() => void refresh()}
          >
            Обновить состояния
          </Button>
          <Button
            type="primary"
            icon={<PlusOutlined />}
            onClick={openCreate}
            disabled={!availableRemote.length && !!remote.length}
          >
            Добавить склад
          </Button>
        </Space>
      </div>

      <Alert
        type="info"
        showIcon
        style={{ marginBottom: 16 }}
        message="Один склад МП можно добавить только один раз."
        description={
          <AntLink onClick={() => setHelpOpen(true)}>
            Как работает отключение склада
          </AntLink>
        }
      />

      <Table
        size="middle"
        loading={loading}
        rowKey="id"
        pagination={false}
        dataSource={rows}
        locale={{ emptyText: 'Нет добавленных складов' }}
        columns={[
          {
            title: 'Склад МП',
            render: (_, r) => (
              <div>
                <div>{r.name}</div>
                <Text type="secondary" style={{ fontSize: 12 }}>
                  {r.external_warehouse_id}
                </Text>
              </div>
            ),
          },
          {
            title: 'Физические склады',
            render: (_, r) => <SelfWarehousesCell row={r} />,
          },
          {
            title: 'Состояние',
            width: 180,
            render: (_, r) => {
              const s = warehouseState(r);
              return <Badge status={s.status} text={s.text} />;
            },
          },
          {
            title: 'Действия',
            width: 220,
            render: (_, r) => {
              const needsSetup = !r.self_warehouse_ids?.length;
              return (
                <Space wrap size={0}>
                  <Button
                    type="link"
                    size="small"
                    onClick={() => openEdit(r)}
                  >
                    {needsSetup ? 'Настроить' : 'Изменить'}
                  </Button>
                  <Button
                    type="link"
                    size="small"
                    onClick={() => setMetaMp(r)}
                  >
                    Лимиты / группы
                  </Button>
                  <Dropdown
                    menu={{
                      items: [
                        r.is_active
                          ? {
                              key: 'off',
                              label: 'Отключить',
                              danger: true,
                              disabled: r.status === 'archived',
                              onClick: () => confirmDisable(r),
                            }
                          : {
                              key: 'on',
                              label: 'Включить',
                              disabled: r.status === 'archived',
                              onClick: () => void enableWarehouse(r),
                            },
                      ],
                    }}
                    trigger={['click']}
                  >
                    <Button type="link" size="small">
                      ⋯
                    </Button>
                  </Dropdown>
                </Space>
              );
            },
          },
        ]}
      />

      <MetaGroupsDrawer
        open={!!metaMp}
        mpWarehouse={metaMp}
        onClose={() => setMetaMp(null)}
        onMpChanged={(updated) => {
          setMetaMp(updated);
          onChanged(rows.map((r) => (r.id === updated.id ? updated : r)));
        }}
      />
      <Drawer
        title={
          drawerMode === 'create' ? 'Добавление склада' : 'Изменение склада'
        }
        open={drawerOpen}
        onClose={closeDrawer}
        width={440}
        destroyOnClose
        extra={
          <Space>
            <Button onClick={closeDrawer}>Отмена</Button>
            <Button
              type="primary"
              loading={saving}
              onClick={() => void saveDrawer()}
            >
              {drawerMode === 'create' ? 'Добавить склад' : 'Сохранить'}
            </Button>
          </Space>
        }
      >
        <Form layout="vertical">
          <Form.Item label="Склад маркетплейса" required>
            {drawerMode === 'create' ? (
              <Select
                showSearch
                optionFilterProp="label"
                placeholder="Выберите склад"
                value={externalId}
                onChange={setExternalId}
                options={availableRemote.map((r) => ({
                  value: r.external_id,
                  label: `${r.name} · ${r.status || 'active'}`,
                }))}
                notFoundContent={
                  remote.length
                    ? 'Все доступные склады уже добавлены'
                    : 'Нет складов с МП — обновите состояния'
                }
              />
            ) : (
              <Text>
                {editing?.name}
                <Text type="secondary">
                  {' '}
                  · {editing?.external_warehouse_id}
                </Text>
              </Text>
            )}
          </Form.Item>
          <Form.Item
            label="Сопоставить с физическими складами"
            required
            extra="Можно выбрать несколько, если они используются как единый склад МП."
          >
            <Select
              mode="multiple"
              showSearch
              optionFilterProp="label"
              placeholder="Физические склады"
              value={selfIds}
              onChange={onSelfIdsChange}
              options={selfOptions}
            />
          </Form.Item>
          {selfIds.length > 1 && marketplace === 'wildberries' ? (
            <Form.Item
              label="Маркеры в названии поставки WB"
              extra="Целое слово в имени поставки, например ИГ или МШ. Пусто — атрибуция по остаткам."
            >
              <Space direction="vertical" style={{ width: '100%' }}>
                {selfIds.map((id) => {
                  const sw = selfWarehouses.find((s) => s.id === id);
                  return (
                    <Space key={id} style={{ width: '100%' }}>
                      <Text style={{ minWidth: 140 }}>{sw?.name || id}</Text>
                      <Input
                        placeholder="ИГ"
                        maxLength={32}
                        value={supplyTokens[id] || ''}
                        onChange={(e) =>
                          setSupplyTokens((prev) => ({
                            ...prev,
                            [id]: e.target.value,
                          }))
                        }
                        style={{ width: 120 }}
                      />
                    </Space>
                  );
                })}
              </Space>
            </Form.Item>
          ) : null}
          <Paragraph type="secondary" style={{ marginBottom: 0 }}>
            Дневные лимиты (группа и по физскладам) — кнопка «Лимиты /
            группы» в строке склада.
          </Paragraph>
        </Form>
      </Drawer>

      <Modal
        open={helpOpen}
        title="Как работает отключение склада"
        onCancel={() => setHelpOpen(false)}
        footer={[
          <Button key="ok" type="primary" onClick={() => setHelpOpen(false)}>
            Понятно
          </Button>,
        ]}
      >
        <Paragraph>
          Отключение склада останавливает синхронизацию остатков и выставляет
          целевой остаток в 0 — на маркетплейс уходят нули, пока склад снова не
          включат.
        </Paragraph>
        <Paragraph>
          Это не удаляет сопоставление с физическими складами. Удалить или
          изменить сопоставление можно через «Изменить».
        </Paragraph>
      </Modal>
    </div>
  );
};

const SelfWarehousesCell = ({ row }: { row: MpWarehouse }) => {
  const list = row.self_warehouses || [];
  if (!list.length) {
    return <Text type="warning">Не настроено</Text>;
  }
  const label = (w: { id: number; name: string; supply_token?: string | null }) =>
    w.supply_token ? `${w.name} · ${w.supply_token}` : w.name;
  if (list.length === 1) {
    return <span>{label(list[0])}</span>;
  }
  const rest = list.slice(1);
  return (
    <span>
      {label(list[0])}{' '}
      <Popover
        content={
          <ul style={{ margin: 0, paddingLeft: 18 }}>
            {rest.map((w) => (
              <li key={w.id}>{label(w)}</li>
            ))}
          </ul>
        }
      >
        <Text type="secondary" style={{ cursor: 'pointer' }}>
          +{rest.length}
        </Text>
      </Popover>
    </span>
  );
};
